import { Component, computed, inject, OnDestroy, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { BaseDataGridComponent } from '@components/base-data-grid.component';
import { dateFormattingOptions } from '@domain/formatting';
import { ServiceProductItem } from '@hjc/masterdata-types';
import { TranslateModule } from '@ngx-translate/core';
import { ServicesService } from '@services/services.service';
import { ColDef, GridApi, RowClickedEvent, themeAlpine } from 'ag-grid-community';
import Fuse from 'fuse.js';
import { debounceTime, Subscription } from 'rxjs';
import { PageHeaderComponent } from '@components/page-header/page-header.component';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { AgGridAngular } from 'ag-grid-angular';

@Component({
  selector: 'md-pricing-services-page',
  standalone: true,
  imports: [PageHeaderComponent, MatFormFieldModule, MatInputModule, MatIconModule, MatButtonModule, AgGridAngular, ReactiveFormsModule, TranslateModule],
  templateUrl: './pricing-services-page.component.html',
  styleUrl: './pricing-services-page.component.scss'
})
export class PricingServicesPageComponent extends BaseDataGridComponent implements OnDestroy {
  private servicesService = inject(ServicesService);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);

  private gridApi = signal<GridApi<ServiceProductItem> | null>(null);
  isLoading = signal<boolean>(false);
  isExporting = signal(false);
  allServices = signal<ServiceProductItem[]>([]);
  displayServices = signal<ServiceProductItem[]>([]);

  searchInput = new FormControl<string>('', { nonNullable: true });
  searchTermSubscription?: Subscription;
  fuse?: Fuse<ServiceProductItem>;
  theme = themeAlpine;

  columnDefs = computed(() => {
    const getTranslatedHeader = (key: string) => (this.translationsReady() ? this.translate.instant(key) : key);

    return [
      { headerName: getTranslatedHeader('FIELDS.IDENTIFIER'), field: 'id', sortable: true, flex: 1 },
      { headerName: getTranslatedHeader('INFO.NAME'), field: `name.${this.lang()}`, sortable: true, flex: 1 },
      { headerName: getTranslatedHeader('FIELDS.SYSTEM.CREATED_BY'), valueGetter: p => p.data?.createdBy, sortable: true, filter: true, flex: 1 },
      {
        headerName: getTranslatedHeader('FIELDS.SYSTEM.CREATED_AT'),
        field: 'createdAt',
        valueFormatter: p => new Date(p.value).toLocaleString(this.lang(), dateFormattingOptions),
        sortable: true,
        flex: 1
      },
      { headerName: getTranslatedHeader('FIELDS.SYSTEM.UPDATED_BY'), valueGetter: p => p.data?.updatedBy, sortable: true, filter: true, flex: 1 },
      {
        headerName: getTranslatedHeader('FIELDS.SYSTEM.UPDATED_AT'),
        field: 'updatedAt',
        valueFormatter: p => new Date(p.value).toLocaleString(this.lang(), dateFormattingOptions),
        sortable: true,
        flex: 1
      }
    ] as ColDef<ServiceProductItem>[];
  });

  constructor() {
    super();
    this.init();
  }

  async init() {
    this.isLoading.set(true);

    try {
      const services = (await this.servicesService.getServices())?.data || [];
      this.allServices.set(services);
      this.displayServices.set(services);

      this.initFuse();
      this.searchTermSubscription = this.searchInput.valueChanges.pipe(debounceTime(300)).subscribe(term => this.search(term));
    } catch (error) {
      console.error('Failed to load services', error);
    } finally {
      this.isLoading.set(false);
    }
  }

  initFuse() {
    this.fuse = new Fuse<ServiceProductItem>(this.allServices(), {
      keys: ['id', `name.${this.lang()}`, 'fillingSpots.id', `byProducts.name.${this.lang()}`, `byProducts.productType.name.${this.lang()}`],
      threshold: 0.3
    });
  }

  search(term: string) {
    if (!this.fuse) {
      return;
    }

    if (!term || term.length === 0) {
      this.displayServices.set(this.allServices());
      return;
    }

    const results = this.fuse.search(term);
    this.displayServices.set(results.map(r => r.item));
  }

  onRowClicked(event: RowClickedEvent) {
    this.router.navigate([event.data.id], { relativeTo: this.activatedRoute });
  }

  onGridReady(params: { api: GridApi<ServiceProductItem> }) {
    this.gridApi.set(params.api);
  }

  async exportToExcel() {
    const api = this.gridApi();
    if (!api) return;

    await this.exportGridToExcel(api, this.columnDefs(), 'byproduct-pricing', this.isExporting);
  }

  ngOnDestroy() {
    this.searchTermSubscription?.unsubscribe();
  }
}
